<script lang="ts">
    import { App } from "@/constants";
    import { growaveConfigurationToken } from "@/tokens";
    import {
        REVIEWS_BRANDING_PRODUCT_AVG_RATING_DISPLAY_STYLE,
        REVIEWS_BRANDING_PRODUCT_AVG_RATING_VISIBILITY,
        SCROLL_CUSTOM_TAB,
        SCROLL_QUESTIONS_TAB,
        SCROLL_REVIEWS_TAB,
    } from "@apps/reviews/constants";
    import ReviewsStorefrontValuesBrandingEntity from "@apps/reviews/entities/ReviewsStorefrontValuesBrandingEntity";
    import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
    import { ProductIdManagerInterface } from "@lib/ProductIdManager/ProductIdManagerInterface";
    import ScrollManager from "@lib/ScrollManager/ScrollManager";
    import { DesignModeProviderInterface } from "@modules/common_module/DesignModeProvider/DesignModeProviderInterface";
    import { GwI18n } from "@modules/i18n/GwI18n/GwI18n";
    import { filter, lastValueFrom, tap } from "rxjs";
    import { onMount } from "svelte";
    import { container } from "tsyringe";

    import ReviewsTheme from "../../components/ReviewsTheme/ReviewsTheme.svelte";
    import AskQuestion from "./components/AskQuestion.svelte";
    import AverageReviews from "./components/AverageReviews.svelte";
    import ReviewsListInfoQuery from "./queries/ReviewsListInfoQuery";
    import ReviewsProductInfoQuery from "./queries/ReviewsProductInfoQuery";
    import ReviewsListInfoService from "./services/ReviewsListInfoService";
    import ReviewsProductInfoService from "./services/ReviewsProductInfoService";

    export let reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    export let reviewsProductInfoService: ReviewsProductInfoService;
    export let reviewsProductInfoQuery: ReviewsProductInfoQuery;
    export let reviewsListInfoQuery: ReviewsListInfoQuery;
    export let reviewsListInfoService: ReviewsListInfoService;
    export let productIdManager: ProductIdManagerInterface;
    export let designModeProvider: DesignModeProviderInterface;
    export let gwI18n: GwI18n;
    export let afterWidgetInject: () => void;
    export let elementToScrollId: string;
    export let elementToClickId: string;

    const productInfo$ = reviewsProductInfoQuery.productInfo$;
    const status$ = reviewsListInfoQuery.status$;
    const ratesInfo$ = reviewsListInfoQuery.ratesInfo$;
    const scrollManager = container.resolve(ScrollManager);
    const reviewsBranding$ =
        reviewsStorefrontValuesService.selectReviewsBrandingOptions();

    const growaveConfiguration = container.resolve(growaveConfigurationToken);

    const isDesignMode = designModeProvider.isDesignMode() === true;
    const isReviewsAppEnabled =
        growaveConfiguration.availableApps.includes(App.Reviews) ||
        isDesignMode;
    const isQuestionsAppEnabled =
        growaveConfiguration.availableApps.includes(App.Questions) ||
        isDesignMode;

    const elementToScroll = document.getElementById(elementToScrollId);
    const elementToClick = document.getElementById(elementToClickId);

    const handleGoToCustomAnchor = () => {
        scrollManager.scrollTo(SCROLL_CUSTOM_TAB);
        if (elementToClick?.hasAttribute("aria-expanded")) {
            const expanded =
                elementToClick?.getAttribute("aria-expanded") === "true";
            if (expanded) return;
        }
        if (elementToClick?.hasAttribute("aria-selected")) {
            const selected =
                elementToClick?.getAttribute("aria-selected") === "true";
            if (selected) return;
        }
        elementToClick?.click();
    };

    const handleGoToQuestions = () => {
        if (elementToScroll) handleGoToCustomAnchor();
        scrollManager.scrollTo(SCROLL_QUESTIONS_TAB);
    };
    const handleGoToReviews = () => {
        if (elementToScroll) handleGoToCustomAnchor();
        scrollManager.scrollTo(SCROLL_REVIEWS_TAB);
    };

    onMount(() => {
        const productIdSubscription = productIdManager.productId$
            .pipe(
                filter((productId): productId is number => productId !== null),
                tap((productId) => {
                    lastValueFrom(
                        reviewsProductInfoService.getReviewsProductInfo({
                            productId,
                        })
                    );

                    lastValueFrom(
                        reviewsListInfoService.getReviewsListInfo({ productId })
                    );
                })
            )
            .subscribe();

        afterWidgetInject();

        if (elementToScroll) {
            scrollManager.register(SCROLL_CUSTOM_TAB, elementToScroll);
        }

        return () => {
            productIdSubscription.unsubscribe();
        };
    });

    const checkWidgetVisibility = (
        reviewsBranding: ReviewsStorefrontValuesBrandingEntity,
        reviewsCount: number | undefined
    ) => {
        if (!reviewsBranding) return false;

        if (
            reviewsBranding.productAvgRatingWidgetSettings.widgetVisibility ===
                REVIEWS_BRANDING_PRODUCT_AVG_RATING_VISIBILITY.HIDE &&
            reviewsCount === 0
        ) {
            return false;
        }

        return true;
    };

    const checkAskQuestionVisibility = (
        reviewsBranding: ReviewsStorefrontValuesBrandingEntity
    ) => {
        if (!isQuestionsAppEnabled || !reviewsBranding) return false;

        if (
            reviewsBranding.productAvgRatingWidgetSettings
                .widgetDisplayStyle ===
            REVIEWS_BRANDING_PRODUCT_AVG_RATING_DISPLAY_STYLE.SHOW_TEXT_STARS_AND_QUESTION
        ) {
            return true;
        }

        return false;
    };

    $: showWidget = checkWidgetVisibility(
        $reviewsBranding$,
        $productInfo$?.reviewsCount
    );
    $: showProductAvgRating = isReviewsAppEnabled;
    $: showAskQuestion = checkAskQuestionVisibility($reviewsBranding$);
</script>

{#if $reviewsBranding$ && showWidget}
    <ReviewsTheme branding={$reviewsBranding$}>
        {#if $productInfo$}
            <div class="gw-rv-average">
                <div class="gw-rv-average__rating">
                    {#if showProductAvgRating}
                        <AverageReviews
                            status={$status$}
                            reviewsCount={$productInfo$.reviewsCount}
                            averageReviewsRating={$productInfo$.averageReviewsRating}
                            ratesInfo={$ratesInfo$}
                            widgetDisplayStyle={$reviewsBranding$
                                .productAvgRatingWidgetSettings
                                .widgetDisplayStyle}
                            showStarsDistributionOnHover={$reviewsBranding$
                                .productAvgRatingWidgetSettings
                                .showStarsDistributionOnHover}
                            on:goToReviews={handleGoToReviews}
                            {gwI18n}
                        />
                    {/if}

                    {#if showProductAvgRating && showAskQuestion}
                        <span class="gw-rv-average__divider"> | </span>
                    {/if}

                    {#if showAskQuestion}
                        <AskQuestion
                            on:goToQuestions={handleGoToQuestions}
                            questionsCount={$productInfo$.questionsCount}
                        />
                    {/if}
                </div>
            </div>
        {/if}
    </ReviewsTheme>
{/if}

<style lang="postcss" global>
    @import "../../../../themes/default/default.css";
    @import "../../../../themes/globals.css";

    .gw-rv-average {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
    }

    .gw-rv-average__rating {
        display: flex;
        align-items: center;
    }

    .gw-rv-average__divider {
        padding: 0 0.5em;
    }
</style>
